import type {
  OrderDetailDTO,
  OrderItemDTO,
} from "@/modules/orders/domain/contracts";

/**
 * โครงสร้างข้อมูลสำหรับหน้ากระดาษพิมพ์ (Receipt / Invoice / Shipping Label)
 * แมปจาก OrderDetailDTO เพื่อให้ Print views ใช้ได้
 */
export interface OrderPrintData {
  orderNumber: string;
  createdAt: string;
  items: Array<{
    id: string;
    productName: string | null;
    variantPrice?: number;
    price: number;
    quantity: number;
    total: number;
    resolvedVariantOptions?: Array<{
      templateName: string;
      valueName: string;
    }> | null;
  }>;
  customer?: {
    name?: string | null;
    phoneNumber?: string | null;
    email?: string | null;
  } | null;
  shipping?: {
    firstName?: string | null;
    lastName?: string | null;
    /** ບ້ານ / ທີ່ຢູ່ (snapshot shipping_village — ແລງ newline ເປັນຊ່ອງວ່າງຕອນແມັບ) */
    address?: string | null;
    district?: string | null;
    province?: string | null;
    phoneNumber?: string | null;
    trackingNumber?: string | null;
    shipmentNumber?: string | null;
    shippingCarrierName?: string | null;
    carrierBranch?: string | null;
  } | null;
  /** ສ່ວນຫຼຸດທ້າຍບິນ (ຫນ່ວຍເລັກສຸດ) */
  discountTotal: number;
  /** ຄ່າສົ່ງ (ຫນ່ວຍເລັກສຸດ); null = ບໍ່ມີ / ຍັງບໍ່ຄຳນວນ */
  shippingFee: number | null;
  /** ຍອດຊຳລະສຸດທ້າຍ (ຫຼັງຫັກສ່ວນຫຼຸດ ແລະ ຄ່າສົ່ງ) */
  grandTotal: number;
  paymentMethod?: string | null;
  paymentStatus: string;
  /** ເບີຕິດຕໍ່ຮ້ານຈາກຕັ້ງຄ່າທົ່ວໄປ (general.phone) — ສຳລັບໃບພິມ */
  contactPhone?: string | null;
  channel?: OrderDetailDTO["channel"];
}

/** Single line for receipt / invoice / label: "carrier · branch". */
export function buildPrintCarrierDisplay(
  shipping: OrderPrintData["shipping"],
): string | null {
  if (!shipping) return null;
  const a = shipping.shippingCarrierName?.trim() || "";
  const b = shipping.carrierBranch?.trim() || "";
  if (!a && !b) return null;
  return [a, b].filter(Boolean).join(" · ");
}

/**
 * ແຂວງ/province → ຂ., ເມືອງ/district → ມ., ບ້ານ/village → ບ.
 * ลำดับ: ແຂວງ → ເມືອງ → ບ້ານ
 */
export function formatPrintBillLocationParts(
  shipping: OrderPrintData["shipping"] | null | undefined,
): string[] {
  if (!shipping) return [];
  const parts: string[] = [];
  const district = (shipping.district ?? "").trim();
  const village = (shipping.address ?? "").trim();
  const province = (shipping.province ?? "").trim();
  if (province) parts.push(`ຂ.${province}`);
  if (district) parts.push(`ມ.${district}`);
  if (village) parts.push(`ບ.${village}`);
  return parts;
}

/**
 * บรรทัดที่อยู่คั่นด้วย comma — สำหรับ invoice / receipt
 */
export function formatPrintBillLocationLine(
  shipping: OrderPrintData["shipping"] | null | undefined,
): string {
  return formatPrintBillLocationParts(shipping).join(", ");
}

export function formatPrintVariantOptions(
  opts: Array<{ templateName: string; valueName: string }> | null | undefined,
): string | null {
  if (!opts?.length) return null;
  const parts = opts
    .map((o) => {
      const t = o.templateName.trim();
      const v = o.valueName.trim();
      if (t && v) return `${t} ${v}`;
      return (t || v).trim();
    })
    .filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

function mapItem(item: OrderItemDTO): OrderPrintData["items"][number] {
  const fromDb = item.resolvedVariantOptions?.filter(
    (o) => o.templateName.trim() || o.valueName.trim(),
  );
  const resolvedVariantOptions =
    fromDb && fromDb.length > 0
      ? fromDb
      : item.variantSku
        ? [{ templateName: "", valueName: item.variantSku }]
        : null;
  return {
    id: item.id,
    productName: item.productName ?? null,
    price: item.price,
    variantPrice: item.price,
    quantity: item.quantity,
    total: item.total,
    resolvedVariantOptions,
  };
}

/**
 * แมป OrderDetailDTO จาก API เป็น OrderPrintData สำหรับพิมพ์
 */
export function mapOrderDetailToPrintData(
  order: OrderDetailDTO,
): OrderPrintData {
  const paymentStatus =
    order.status === "completed"
      ? "paid"
      : order.status === "cancelled"
        ? "cancelled"
        : order.status === "returned"
          ? "returned"
          : order.status === "return_in_transit"
            ? "return_in_transit"
            : "pending";

  return {
    orderNumber: order.code,
    createdAt: order.createdAt,
    items: order.items.map(mapItem),
    customer: {
      name: order.customerName ?? null,
      phoneNumber: order.customerPhone ?? null,
      email: order.customerEmail ?? null,
    },
    shipping: (() => {
      const addr = order.shipping;
      return {
        trackingNumber: order.trackingNumber ?? null,
        shipmentNumber:
          order.halShipment?.shipmentNumber ?? order.trackingNumber ?? null,
        phoneNumber: addr?.shippingPhone ?? order.customerPhone ?? null,
        address: addr?.shippingVillage?.trim()
          ? addr.shippingVillage.replace(/\s*\n\s*/g, " ").trim()
          : null,
        district: addr?.shippingDistrictName ?? null,
        province: addr?.shippingProvinceName ?? null,
        firstName: addr?.shippingRecipientName ?? null,
        lastName: null,
        shippingCarrierName: addr?.shippingCarrierName ?? null,
        carrierBranch: addr?.carrierBranch ?? null,
      };
    })(),
    discountTotal: order.discountTotal ?? 0,
    shippingFee: order.shippingFee,
    grandTotal: order.total,
    paymentMethod: order.paymentMethod ?? null,
    paymentStatus,
    channel: order.channel,
  };
}
